import { computed, ref, type ComputedRef, type Ref } from 'vue';
import LocalStorage from 'Core/commons/LocalStorage';
import { goToUrl } from 'vueDir/helpers';
import type { OcrQueueRow } from 'composables/ocr/types';

interface UseOcrNavigationQueueReturn {
    ocrRows: Ref<OcrQueueRow[]>;
    multipleVerify: Ref<boolean>;
    totalRows: Ref<number>;
    completeRows: ComputedRef<number>;
    goNextOcr: (rowsRemaining: OcrQueueRow[], nextIndex: number) => void;
    clearQueue: () => void;
    rowsExcluding: (ocrId: string | null) => OcrQueueRow[];
}

interface PersistedQueue {
    rows?: OcrQueueRow[];
    multipleVerify?: boolean;
    totalRows?: number;
}

export const useOcrNavigationQueue = (): UseOcrNavigationQueueReturn => {
    const storage = new LocalStorage('ocr', 'form.verification');
    const persisted = (storage.get() || {}) as PersistedQueue;

    const ocrRows = ref<OcrQueueRow[]>(Array.isArray(persisted.rows) ? persisted.rows : []);
    const multipleVerify = ref<boolean>(persisted.multipleVerify || false);
    const totalRows = ref<number>(persisted.totalRows || 0);

    const completeRows = computed<number>(() => totalRows.value - ocrRows.value.length);

    const rowsExcluding = (ocrId: string | null): OcrQueueRow[] =>
        ocrRows.value.filter((row) => row.id.toString() !== ocrId);

    const goNextOcr = (rowsRemaining: OcrQueueRow[], nextIndex: number): void => {
        ocrRows.value = rowsRemaining;
        storage.set({
            rows: rowsRemaining,
            multipleVerify: multipleVerify.value,
            totalRows: totalRows.value,
        });
        goToUrl(`/purchase/incoming/verification/${rowsRemaining[nextIndex].id}`);
    };

    const clearQueue = (): void => {
        storage.delete();
    };

    return {
        ocrRows,
        multipleVerify,
        totalRows,
        completeRows,
        goNextOcr,
        clearQueue,
        rowsExcluding,
    };
};
